import React, { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { Formik, Form } from "formik";
import ChargeSection from "../capture/ReceivingWizard/AdditionalCharges/ChargeSection";
import RateMultiSelectModal from "../capture/ReceivingWizard/AdditionalCharges/RateMultiSelectModal";
import useMutation from "../../hooks/useMutation";
import {
  UPDATE_INVENTORY_REQUEST_MUTATION,
  UPDATE_INTERNAL_WORK_ORDER_MUTATION,
} from "../../constants/graphql/mutations";
import useNotification from "../../hooks/notification";

const sectionToRateKey = {
  laborCharges: "laborRates",
  materialCharges: "materialRates",
  equipmentCharges: "equipmentRates",
};

export default function InventoryRequestCategoryChargesEditModal({
  open,
  onClose,
  title,
  charges = {},
  project,
  inventoryRequest,
  categoryKey,
  entityType = "inventoryRequest",
}) {
  const [openRatePicker, setOpenRatePicker] = useState(true);
  const mutation = entityType === "internalWorkOrder"
    ? UPDATE_INTERNAL_WORK_ORDER_MUTATION
    : UPDATE_INVENTORY_REQUEST_MUTATION;
  const [updateEntity, { loading }] = useMutation(mutation);
  const { notifySuccess, notifyError } = useNotification();

  const rateKey = sectionToRateKey[categoryKey];
  const canAddCharges = !!rateKey && !!project && !!inventoryRequest;
  const chargeKeys = Object.keys(charges).filter(key => !["calculated_at", "total"].includes(key));

  const initialValues = useMemo(() => {
    const values = {};
    if (canAddCharges) {
      chargeKeys.forEach(key => {
        values[key] = charges[key]?.quantity || "";
      });
    }
    return { [categoryKey]: values };
  }, [canAddCharges, categoryKey, chargeKeys, charges]);

  const handleSubmit = async values => {
    try {
      const sectionValues = values?.[categoryKey] || {};
      const cleanedValues = {};
      Object.entries(sectionValues).forEach(([k, v]) => {
        if (v !== "" && v !== undefined && v !== null) {
          cleanedValues[k] = Number(v);
        }
      });

      const resultKey = entityType === "internalWorkOrder"
        ? "updateInternalWorkOrder"
        : "updateInventoryRequest";

      const result = await updateEntity({
        variables: {
          id: inventoryRequest.id,
          input: {
            [categoryKey]: cleanedValues,
          },
        },
      });

      const errors = result?.data?.[resultKey]?.errors;
      if (errors?.length) {
        notifyError(errors[0]?.message || "Failed to update charges");
        return;
      }

      notifySuccess("Charges updated successfully");
      onClose();
    } catch (err) {
      console.error(err);
      notifyError("Failed to update charges");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ position: "relative" }}>
        <Typography fontSize={20} fontWeight={500} color="text.primary" align="center">
          {title}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: theme => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
          {({ values }) => (
            <Form style={{ display: "flex", flexDirection: "column", height: "100%" }}>
              <Box mt={2}>
                <ChargeSection
                  section={{
                    key: categoryKey,
                    entries: Object.entries(values?.[categoryKey] || {}),
                  }}
                />
              </Box>

              <Box mt={2}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenRatePicker(true)}
                  disabled={!canAddCharges}
                >
                  Add More Charges
                </Button>
              </Box>

              <Box display="flex" justifyContent="flex-end" mt={3} gap={1}>
                <Button variant="outlined" onClick={onClose} disabled={loading}>
                  Cancel
                </Button>
                <Button variant="contained" type="submit" disabled={loading || !canAddCharges}>
                  Save Charges
                </Button>
              </Box>

              {openRatePicker && canAddCharges && (
                <RateMultiSelectModal
                  categoryKey={rateKey}
                  rates={project?.[rateKey]}
                  initialSelected={Object.keys(values?.[categoryKey] || {})}
                  onClose={() => setOpenRatePicker(false)}
                />
              )}
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
